<template>
  <div style="display: flex; flex-direction: column; background: #F8F8F9;">
    <!-- Toolbar -->
    <div class="cm-toolbar">
      <h2>Daily Operations</h2>

      <!-- Date stepper -->
      <div class="cm-stepper">
        <button class="cm-icon-btn" @click="shiftRange(-1)"><FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 10px;" /></button>
        <input v-model="dateFrom" class="cm-input cm-input-sm" type="date" style="width: 108px; text-align: center;" @change="fetchData" />
        <span style="color: #A5A5A6; font-size: 11px;">→</span>
        <input v-model="dateTo" class="cm-input cm-input-sm" type="date" style="width: 108px; text-align: center;" @change="fetchData" />
        <button class="cm-icon-btn" :disabled="isToday" @click="shiftRange(1)"><FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 10px;" /></button>
      </div>

      <!-- Quick range -->
      <div class="cm-seg">
        <button
          v-for="preset in rangePresets"
          :key="preset.label"
          :class="{ on: isActivePreset(preset) }"
          @click="applyPreset(preset)"
        >{{ preset.label }}</button>
      </div>

      <span style="font-size: 11.5px; font-weight: 600;" :style="{ color: isToday ? '#1F9A63' : '#A5A5A6' }">
        {{ rangeLabel }}
      </span>

      <div style="margin-left: auto; display: flex; align-items: center; gap: 8px;">
        <button class="cm-icon-btn" :disabled="loading" title="Refresh" @click="fetchData">
          <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 11px;" />
        </button>
      </div>
    </div>

    <!-- Filters: program (chips) + care manager (multi-select). Client-side over
         the already-loaded matrix; blank CMs/roles are dropped automatically. -->
    <div
      v-if="!loading && (matrix.roles || []).length"
      style="display:flex; align-items:center; gap:12px; flex-wrap:wrap; padding: 10px 16px 0;"
    >
      <div class="cm-seg">
        <button :class="{ on: !programFilter.length }" @click="programFilter = []">All programs</button>
        <button
          v-for="p in availablePrograms"
          :key="'pf-' + p"
          :class="{ on: programFilter.includes(p) }"
          @click="toggleProgram(p)"
        >{{ p }}</button>
      </div>

      <SimpleMultiSelect
        v-model="cmFilter"
        :options="cmOptions"
        :sensitive-labels="true"
        placeholder="All care managers"
        :searchable="true"
        :multiple="true"
        style="min-width: 200px;"
      />
      <button
        v-if="cmFilter.length || programFilter.length"
        class="cm-input cm-input-sm"
        style="cursor:pointer;"
        @click="programFilter = []; cmFilter = [];"
      >Clear</button>
    </div>

    <!-- Summary stat chips (from the merged Daily Operations view). Reflect the
         active program + care-manager filters. -->
    <div
      v-if="!loading && hasData"
      style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap; padding: 14px 16px 0;"
    >
      <div class="cm-stat"><span class="cm-stat-label">Roster</span><span class="cm-stat-value">{{ summary.roster }}</span></div>
      <div class="cm-stat"><span class="cm-stat-label">Due Today</span><span class="cm-stat-value">{{ summary.due }}</span></div>
      <div class="cm-stat is-info"><span class="cm-stat-label">New</span><span class="cm-stat-value">{{ summary.new }}</span></div>
      <div class="cm-stat is-ok"><span class="cm-stat-label">Actioned</span><span class="cm-stat-value">{{ summary.actioned }}</span></div>
      <div class="cm-stat"><span class="cm-stat-label">End of Day</span><span class="cm-stat-value">{{ summary.end }}</span></div>
      <div class="cm-stat is-brand"><span class="cm-stat-label">Completion</span><span class="cm-stat-value">{{ summary.rate }}%</span></div>
    </div>

    <!-- Legend -->
    <div v-if="!loading && hasData" style="display:flex; align-items:center; gap:14px; padding: 10px 16px 0; font-size: 10.5px; color:#8B8B8D;">
      <span><b style="color:#353537;">R</b> Roster</span>
      <span><b style="color:#353537;">S</b> Start of Day</span>
      <span><b style="color:#2563EB;">+</b> New In</span>
      <span><b style="color:#1F9A63;">✓</b> Completed</span>
      <span><b style="color:#353537;">E</b> Ending</span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="cm-loading"><div class="cm-spinner"></div></div>

    <!-- Role sections -->
    <template v-else-if="hasData">
      <div v-for="section in filteredRoles" :key="section.role" style="padding: 14px 16px 0;">
        <div style="display:flex; align-items:center; gap:8px; margin-bottom: 8px;">
          <span class="role-pill" :style="rolePillStyle(section.role)">{{ section.role }}</span>
          <span style="font-size: 11px; color:#A5A5A6;">{{ section.care_managers.length }} care manager{{ section.care_managers.length === 1 ? '' : 's' }}</span>
        </div>

        <div class="cm-panel" style="display:inline-block; min-width:100%; overflow-x:auto;">
          <table class="cm-table" style="border-collapse: separate; border-spacing: 0;">
            <thead>
              <!-- Tier group headers -->
              <tr>
                <th style="position: sticky; left: 0; z-index: 3; background: #F8F8F9; min-width: 156px;"></th>
                <th
                  v-for="tier in section.tiers"
                  :key="'grp-' + section.role + tier"
                  :colspan="5"
                  class="ctr"
                  style="border-left: 2px solid #E8E9E9;"
                >
                  <span style="display:inline-flex; align-items:center; gap:5px;">
                    <span style="width:7px;height:7px;border-radius:50%;" :style="{ background: tierColor(tier) }"></span>
                    <span style="color:#353537;">{{ tierLabel(tier) }}</span>
                  </span>
                </th>
                <th :colspan="5" class="ctr" style="border-left: 2px solid #D8D8DA;"><span style="color:#353537;">All Tiers</span></th>
              </tr>
              <!-- Metric sub-headers -->
              <tr>
                <th style="position: sticky; left: 0; z-index: 3; background: #F8F8F9;">Care Manager</th>
                <template v-for="tier in [...section.tiers, '_total']" :key="'sub-' + section.role + tier">
                  <th class="num" :style="{ borderLeft: '2px solid ' + (tier === '_total' ? '#D8D8DA' : '#E8E9E9'), paddingLeft: '6px', paddingRight: '6px' }" title="Roster"><span style="color:#8B8B8D;font-size:10px;">R</span></th>
                  <th class="num" style="padding-left:6px;padding-right:6px;" title="Start of day"><span style="color:#8B8B8D;font-size:10px;">S</span></th>
                  <th class="num" style="padding-left:6px;padding-right:6px;" title="New in"><span style="color:#2563EB;font-size:10px;">+</span></th>
                  <th class="num" style="padding-left:6px;padding-right:6px;" title="Completed"><span style="color:#1F9A63;font-size:10px;">✓</span></th>
                  <th class="num" style="padding-left:6px;padding-right:6px;" title="Ending"><span style="color:#8B8B8D;font-size:10px;">E</span></th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr v-for="cm in section.care_managers" :key="section.role + cm">
                <td
style="position: sticky; left: 0; z-index: 1; border-bottom: 1px solid #F0F0F1;"
                  :style="{ background: cm === 'Unassigned' ? '#FDF4E7' : '#FFFFFF' }">
                  <span style="display:inline-flex; align-items:center; gap:8px;">
                    <span v-if="cm === 'Unassigned'" style="width:22px;height:22px;display:inline-flex;align-items:center;justify-content:center;color:#B45309;"><FontAwesomeIcon :icon="['fas','exclamation-triangle']" style="font-size:11px;" /></span>
                    <span v-else class="cm-avatar" :style="avatarStyle(cm)">{{ avatarInitials(cm) }}</span>
                    <span :style="{ fontWeight: 500, color: cm === 'Unassigned' ? '#B45309' : '#353537' }">{{ formatName(cm) }}</span>
                  </span>
                </td>
                <template v-for="tier in [...section.tiers, '_total']" :key="section.role + cm + tier">
                  <td
v-for="(m, mi) in METRICS" :key="section.role + cm + tier + m"
                    class="num"
                    :style="{
                      borderLeft: mi === 0 ? '2px solid ' + (tier === '_total' ? '#D8D8DA' : '#E8E9E9') : undefined,
                      paddingLeft: '6px', paddingRight: '6px',
                      color: cellColor(cell(section, cm, tier, m), m),
                      fontWeight: (m === 'new' || m === 'done' || tier === '_total') ? 600 : 400,
                    }">{{ cell(section, cm, tier, m) || '' }}</td>
                </template>
              </tr>

              <!-- Section totals -->
              <tr>
                <td style="position: sticky; left: 0; z-index: 1; background:#F8F8F9; font-weight:700; color:#353537; border-top: 2px solid #D8D8DA;">Total</td>
                <template v-for="tier in [...section.tiers, '_total']" :key="'tot-' + section.role + tier">
                  <td
v-for="(m, mi) in METRICS" :key="'tot' + section.role + tier + m" class="num"
                    :style="{
                      borderLeft: mi === 0 ? '2px solid ' + (tier === '_total' ? '#D8D8DA' : '#E8E9E9') : undefined,
                      paddingLeft: '6px', paddingRight: '6px',
                      background: '#F8F8F9', borderTop: '2px solid #D8D8DA', fontWeight: 700,
                      color: colTotal(section, tier, m) ? (m === 'new' ? '#2563EB' : m === 'done' ? '#1F9A63' : '#353537') : '#DBDBDC',
                    }">{{ colTotal(section, tier, m) || '' }}</td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div style="height: 16px;"></div>
    </template>

    <!-- Empty -->
    <div v-else class="cm-empty">
      <FontAwesomeIcon :icon="['fas', 'table']" class="ico" />
      <div class="msg">No operations data for this date range</div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import SimpleMultiSelect from '@/components/widgetcomponents/Dropdown_Selector/SimpleMultiSelect.vue';

const TIER_COLORS = {
  'TOC ED HIGH RISK': '#DC2626', 'TOC ED MED RISK': '#F59E0B', 'TOC ED LOW RISK': '#9CA3AF',
  'TOC ED Private Payer': '#8B5CF6',
  'TOC IP HIGH LACE': '#DC2626', 'TOC IP MED LACE': '#F59E0B', 'TOC IP LOW LACE': '#9CA3AF',
  'TOC IP Private Payer': '#8B5CF6', 'TOC No Admit': '#78716C',
  'VC+ Acuity A': '#DC2626', 'VC+ Acuity B': '#F59E0B', 'VC+ Acuity C': '#3B82F6', 'VC+ Acuity D': '#9CA3AF',
  'VC+ RN Initial': '#7C3AED', 'VC+ Pre-Screen': '#A78BFA', 'VC+ TOC': '#3B82F6',
  'Transitions of Care': '#0EA5E9', 'High Utilizer': '#E11D48',
  'CDM': '#6366F1', 'SNF': '#EC4899', 'Direct Referral': '#14B8A6', 'LCSW': '#F97316', 'Other': '#6B7280',
};
const TIER_LABELS = {
  'TOC ED HIGH RISK': 'ED High', 'TOC ED MED RISK': 'ED Med', 'TOC ED LOW RISK': 'ED Low',
  'TOC ED Private Payer': 'ED Private',
  'TOC IP HIGH LACE': 'IP High', 'TOC IP MED LACE': 'IP Med', 'TOC IP LOW LACE': 'IP Low',
  'TOC IP Private Payer': 'IP Private', 'TOC No Admit': 'No Admit',
  'VC+ Acuity A': 'Acuity A', 'VC+ Acuity B': 'Acuity B', 'VC+ Acuity C': 'Acuity C', 'VC+ Acuity D': 'Acuity D',
  'VC+ RN Initial': 'VC+ Initial', 'VC+ Pre-Screen': 'VC+ Pre-Screen', 'VC+ TOC': 'VC+ TOC',
  'Transitions of Care': 'Transitions', 'High Utilizer': 'High Utilizer',
};
const ROLE_COLORS = {
  'Health Coach': { c: '#1D4ED8', bg: '#DBEAFE' },
  'RN': { c: '#047857', bg: '#D1FAE5' },
  'LCSW': { c: '#C2410C', bg: '#FFEDD5' },
  'Pharmacist': { c: '#6D28D9', bg: '#EDE9FE' },
  'MA': { c: '#0E7490', bg: '#CFFAFE' },
  'Other': { c: '#6B7280', bg: '#F3F4F6' },
};

// Map each fine-grained tier to a top-level PROGRAM for the program filter.
// (Jess's nurses don't do ED TOC etc. — filter the matrix to the programs a
// viewer cares about; drops the columns AND any CM/role left with nothing.)
function tierProgram(tier) {
  if (tier.startsWith('TOC ED')) return 'TOC ED';
  if (tier.startsWith('TOC IP') || tier === 'TOC No Admit') return 'TOC IP';
  if (tier.startsWith('VC+') || tier === 'VC+') return 'VC+';
  if (tier === 'CDM') return 'CDM';
  if (tier === 'SNF') return 'SNF';
  if (tier === 'LCSW') return 'LCSW';
  if (tier === 'Direct Referral') return 'Direct Referral';
  if (tier === 'Transitions of Care') return 'Transitions of Care';
  if (tier === 'High Utilizer') return 'High Utilizer';
  return 'Other';
}
const PROGRAM_ORDER = ['TOC IP', 'TOC ED', 'Transitions of Care', 'High Utilizer', 'VC+', 'CDM', 'SNF', 'LCSW', 'Direct Referral', 'Other'];
const METRIC_KEYS = ['roster', 'start', 'new', 'done', 'end'];

export default {
  name: 'CareManagementDailyOpsMatrix',
  components: { FontAwesomeIcon, SimpleMultiSelect },
  data() {
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return {
      loading: false,
      matrix: { roles: [] },
      dateFrom: today,
      dateTo: today,
      // Program + care-manager selections persist across navigation so a user
      // doesn't have to re-select them every time they leave and return to the
      // dashboard (Chris Weigert, 2026-07-20). [] = all. Date deliberately NOT
      // persisted — it resets to today each visit, matching Daily Operations.
      programFilter: (() => {
        try {
          const saved = localStorage.getItem('cm_daily_ops_matrix_program_filter');
          if (saved) { const p = JSON.parse(saved); if (Array.isArray(p)) return p; }
        } catch (e) { /* ignore */ }
        return [];
      })(),
      cmFilter: (() => {
        try {
          const saved = localStorage.getItem('cm_daily_ops_matrix_cm_filter');
          if (saved) { const p = JSON.parse(saved); if (Array.isArray(p)) return p; }
        } catch (e) { /* ignore */ }
        return [];
      })(),
    };
  },
  computed: {
    hasData() { return (this.filteredRoles || []).length > 0; },

    // All programs present in the current data (for the program filter chips).
    availablePrograms() {
      const set = new Set();
      for (const section of (this.matrix.roles || [])) {
        for (const t of (section.tiers || [])) set.add(tierProgram(t));
      }
      return PROGRAM_ORDER.filter(p => set.has(p));
    },

    // All care managers present (for the CM filter).
    availableCms() {
      const set = new Set();
      for (const section of (this.matrix.roles || [])) {
        for (const cm of (section.care_managers || [])) set.add(cm);
      }
      return Array.from(set).sort((a, b) => (a === 'Unassigned') - (b === 'Unassigned') || a.localeCompare(b));
    },
    cmOptions() {
      return this.availableCms.map(cm => ({ value: cm, label: cm }));
    },

    // The matrix filtered by program + care manager, with blank CMs/roles
    // dropped and totals recomputed over only the visible cells (2a/2c).
    filteredRoles() {
      const progSel = this.programFilter;
      const cmSel = this.cmFilter;
      const out = [];
      for (const section of (this.matrix.roles || [])) {
        // Keep only tiers in the selected program(s).
        const tiers = (section.tiers || []).filter(t => !progSel.length || progSel.includes(tierProgram(t)));
        if (!tiers.length) continue;

        // Keep only selected CMs that have data in the surviving tiers.
        const cms = (section.care_managers || []).filter(cm => {
          if (cmSel.length && !cmSel.includes(cm)) return false;
          return tiers.some(t => {
            const c = section.data?.[cm]?.[t];
            return c && METRIC_KEYS.some(k => (c[k] || 0) > 0);
          });
        });
        if (!cms.length) continue;

        // Recompute per-CM totals + column totals over the visible tiers only.
        const data = {};
        const colTotals = {};
        for (const t of tiers) colTotals[t] = { roster: 0, start: 0, new: 0, done: 0, end: 0 };
        const grand = { roster: 0, start: 0, new: 0, done: 0, end: 0 };
        for (const cm of cms) {
          const block = {};
          const cmTotal = { roster: 0, start: 0, new: 0, done: 0, end: 0 };
          for (const t of tiers) {
            const m = section.data?.[cm]?.[t];
            if (!m) continue;
            block[t] = m;
            for (const k of METRIC_KEYS) {
              cmTotal[k] += m[k] || 0;
              colTotals[t][k] += m[k] || 0;
              grand[k] += m[k] || 0;
            }
          }
          block._total = cmTotal;
          data[cm] = block;
        }
        colTotals._total = grand;
        out.push({ role: section.role, tiers, care_managers: cms, data, column_totals: colTotals });
      }
      return out;
    },

    // Summary stat chips (Roster / Due Today / New / Actioned / End of Day /
    // Completion%) — carried over from the old flat "Daily Operations" dashboard
    // when the two were merged (Chris Weigert, 2026-07-20). Summed client-side
    // over the VISIBLE (filtered) role sections' grand totals, so the chips track
    // the program + care-manager filters exactly like the table does. Completion =
    // Actioned (done) / Due Today (start), matching CareManagement_Operations.
    summary() {
      const s = { roster: 0, start: 0, new: 0, done: 0, end: 0 };
      for (const section of this.filteredRoles) {
        const g = section.column_totals?._total;
        if (!g) continue;
        for (const k of METRIC_KEYS) s[k] += g[k] || 0;
      }
      return {
        roster: s.roster,
        due: s.start,
        new: s.new,
        actioned: s.done,
        end: s.end,
        rate: s.start ? Math.round((s.done / s.start) * 100) : 0,
      };
    },
    isToday() { const now = new Date(); return this.dateTo === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`; },
    isSingleDay() { return this.dateFrom === this.dateTo; },
    rangeLabel() {
      if (this.isSingleDay) {
        if (this.isToday) return 'Today';
        const d = new Date(this.dateTo + 'T12:00:00');
        return `${['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]} ${d.getMonth()+1}/${d.getDate()}`;
      }
      return `${Math.round((new Date(this.dateTo+'T12:00:00') - new Date(this.dateFrom+'T12:00:00')) / 86400000) + 1} days`;
    },
    rangePresets() {
      const now = new Date();
      const fmt = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const daysAgo = n => { const d = new Date(now); d.setDate(d.getDate() - n); return fmt(d); };
      return [{ label: 'Today', from: fmt(now), to: fmt(now) }, { label: '3d', from: daysAgo(2), to: fmt(now) }, { label: '7d', from: daysAgo(6), to: fmt(now) }];
    },
  },
  watch: {
    // Persist the program + CM selections so they survive navigation
    // (Chris Weigert, 2026-07-20).
    programFilter: {
      handler(val) { try { localStorage.setItem('cm_daily_ops_matrix_program_filter', JSON.stringify(val)); } catch (e) { /* ignore */ } },
      deep: true,
    },
    cmFilter: {
      handler(val) { try { localStorage.setItem('cm_daily_ops_matrix_cm_filter', JSON.stringify(val)); } catch (e) { /* ignore */ } },
      deep: true,
    },
  },
  created() {
    this.METRICS = ['roster', 'start', 'new', 'done', 'end'];
  },
  mounted() { this.fetchData(); },
  methods: {
    toggleProgram(p) {
      const i = this.programFilter.indexOf(p);
      if (i === -1) this.programFilter.push(p);
      else this.programFilter.splice(i, 1);
    },
    tierColor(t) { return TIER_COLORS[t] || '#6B7280'; },
    tierLabel(t) { return TIER_LABELS[t] || t; },
    rolePillStyle(role) {
      const r = ROLE_COLORS[role] || ROLE_COLORS.Other;
      return { color: r.c, background: r.bg, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 700 };
    },
    shiftRange(direction) {
      const from = new Date(this.dateFrom + 'T12:00:00'), to = new Date(this.dateTo + 'T12:00:00');
      const span = Math.round((to - from) / 86400000) + 1;
      from.setDate(from.getDate() + direction * span); to.setDate(to.getDate() + direction * span);
      const now = new Date(); now.setHours(23, 59, 59, 999);
      if (to > now) return;
      this.dateFrom = from.toISOString().split('T')[0]; this.dateTo = to.toISOString().split('T')[0];
      this.fetchData();
    },
    applyPreset(preset) { this.dateFrom = preset.from; this.dateTo = preset.to; this.fetchData(); },
    isActivePreset(preset) { return this.dateFrom === preset.from && this.dateTo === preset.to; },
    async fetchData() {
      this.loading = true;
      try {
        const { data } = await this.$axios.post('/api/care-management/operations-matrix/', { date_from: this.dateFrom, date_to: this.dateTo });
        this.matrix = data && data.roles ? data : { roles: [] };
      } catch (e) { console.error('Failed to fetch daily ops matrix:', e); this.matrix = { roles: [] }; }
      finally { this.loading = false; }
    },
    cell(section, cm, tier, metric) {
      const v = section.data?.[cm]?.[tier]?.[metric] || 0;
      return v > 0 ? v : '';
    },
    colTotal(section, tier, metric) {
      const v = section.column_totals?.[tier]?.[metric] || 0;
      return v > 0 ? v : '';
    },
    cellColor(v, k) { return !v ? '#DBDBDC' : (k === 'new' ? '#2563EB' : k === 'done' ? '#1F9A63' : '#353537'); },
    formatName(name) {
      if (!name) return '—';
      if (name.includes(',')) { const [last, first] = name.split(',').map(s => s.trim()); return `${this.cap(first)} ${this.cap(last)}`; }
      return name;
    },
    cap(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : ''; },
    avatarInitials(name) {
      const parts = this.formatName(name).split(' ');
      return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
    },
    avatarStyle(name) {
      const hue = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
      return { color: `hsl(${hue} 42% 34%)`, background: `hsl(${hue} 44% 92%)` };
    },
  },
};
</script>
